import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Alert, Button, Typography } from '@evaneos/b2b-ui';
import { FormattedMessage, useIntl } from 'react-intl';

import { ERROR, PENDING } from '../../../design-system/constants';
import { Loader } from '../../../design-system/Loader';
import { GlobalLoader } from '../../../shared/components/layout/GlobalLoader';
import {
    getContractAPICallStatus,
    fetchContract as fetchContractAction,
    fetchLegalInformation as fetchLegalInformationAction,
} from '../../../resources/contract';
import { DossierId } from '../../../resources/dossier/types';
import { useAppDispatch, useAppSelector } from '../../../shared/redux';

import { useSidePanel } from '../SidePanel/SidePanelContext';
import { SidePanelViewMode } from '../SidePanel/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../shared/analytics';

import { ContractView } from './View/ContractView';
import { ContractEdit } from './Edit/ContractEdit';

type ContractProps = {
    viewMode?: SidePanelViewMode;
};

export function Contract({ viewMode = 'compact' }: Readonly<ContractProps>) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { formatMessage } = useIntl();
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { isOpen } = useSidePanel();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    // Check URL param to determine initial editing state
    const urlParams = new URLSearchParams(location.search);
    const contractModeFromUrl = urlParams.get('contractMode');
    const [isEditing, setIsEditing] = useState(contractModeFromUrl === 'editing');

    const status = useAppSelector(getContractAPICallStatus);

    useEffect(() => {
        if (isOpen) {
            trackEvent((ampli, defaultProperties) => {
                ampli.panelContractViewed({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }
    }, [isOpen, trackEvent, trackedTripProperties]);

    const fetchContract = useCallback(() => {
        dispatch(fetchContractAction(dossierId));
    }, [dispatch, dossierId]);

    const fetchLegalInformation = useCallback(() => {
        dispatch(fetchLegalInformationAction(dossierId));
    }, [dispatch, dossierId]);

    useEffect(() => {
        fetchContract();
        fetchLegalInformation();
    }, [fetchContract, fetchLegalInformation]);

    const removeContractModeParam = useCallback(() => {
        const urlParams = new URLSearchParams(location.search);
        urlParams.delete('contractMode');
        const newSearch = urlParams.toString();
        navigate(
            {
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : '',
            },
            { replace: true },
        );
    }, [navigate, location.pathname, location.search]);

    // Clean up URL param when drawer closes
    useEffect(() => {
        if (!isOpen && contractModeFromUrl === 'editing') {
            removeContractModeParam();
        }
    }, [isOpen, contractModeFromUrl, removeContractModeParam]);

    const handleSuccess = useCallback(() => {
        setIsEditing(false);
        if (contractModeFromUrl === 'editing') {
            removeContractModeParam();
        }
        fetchContract(); // Refresh contract data to show updated view
    }, [contractModeFromUrl, fetchContract, removeContractModeParam]);

    if (status === PENDING) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    p: 3,
                    fontSize: '1.6rem',
                }}
            >
                <Loader />
                <GlobalLoader />
            </Box>
        );
    }

    if (status === ERROR) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h2">
                    {formatMessage({ id: 'contract.titles.contract' })}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                    }}
                >
                    <Alert severity="error" sx={{ width: '100%' }}>
                        <FormattedMessage id="global.error.failed_fetch" />
                        <Box sx={{ mt: 2 }}>
                            <Button onClick={() => fetchContract()} color="error">
                                <FormattedMessage id="global.refresh" />
                            </Button>
                        </Box>
                    </Alert>
                </Box>
            </Box>
        );
    }

    if (isEditing) {
        return (
            <Box data-compact-view={viewMode} sx={{ height: '100%' }}>
                <ContractEdit
                    contractId={dossierId}
                    onSuccess={handleSuccess}
                    viewMode={viewMode}
                />
            </Box>
        );
    }

    return (
        <Box data-compact-view={viewMode} sx={{ height: '100%' }}>
            <ContractView
                contractId={dossierId}
                onEdit={() => setIsEditing(true)}
                viewMode={viewMode}
            />
        </Box>
    );
}
